import {useCallback, useEffect, useState} from 'react';

import {throttle} from '@pexip/utils';
import {createSignal} from '@pexip/signal';

export const UI_INACTIVITY_TIMEOUT = 5000;
export const TAP_MAX_DURATION = 300; // ms
export const TAP_MAX_DISTANCE = 10; // px

export type AutoHideUIInterfacesState = 'visible' | 'hidden';

export const autoHideUIInterfacesStateSignal =
    createSignal<AutoHideUIInterfacesState>({
        name: 'autoHideUIInterfaces:state',
    });

/**
 * Hook to signal display state (show/hide) based on UI inactivity timeout
 * @param isStreamReady - only apply autoHide if we've got a ready stream
 * @param isDisabledByConfig - if true then auto hiding is disabled, UI controls will not be hidden
 * @param shouldDisplay - callback to signal when to hide and when to show
 * @returns control to enable/disable auto hide functionality
 */
export const useAutoHideUIInterfaces = (
    isStreamReady: boolean,
    isDisabledByConfig: boolean,
    shouldDisplay?: (shouldDisplay: boolean) => void,
) => {
    const [enabled, setEnabled] = useState(true);

    const enableAutoHideConstraintBased = useCallback((constraint: boolean) => {
        if (!constraint) {
            setEnabled(true);
        }
    }, []);

    const isTouchLikePointer = useCallback(
        (e: PointerEvent) =>
            e.pointerType === 'pen' || e.pointerType === 'touch',
        [],
    );

    useEffect(() => {
        let ignore = false;
        let timeout: ReturnType<typeof setTimeout>;

        // for mobile
        let tapStartTime = 0;
        let tapStartX = 0;
        let tapStartY = 0;

        const timeoutHandler = () => {
            shouldDisplay?.(false);
            document.body.dataset.uiHidden = '';
            autoHideUIInterfacesStateSignal.emit('hidden');
            document.addEventListener('keydown', tabKeydown, {once: true});
        };

        const onPointerMoveCallback = (e?: PointerEvent) => {
            if (ignore || (e && isTouchLikePointer(e))) {
                return;
            }

            clearTimeout(timeout);

            shouldDisplay?.(true);
            delete document.body.dataset.uiHidden;
            autoHideUIInterfacesStateSignal.emit('visible');

            timeout = setTimeout(timeoutHandler, UI_INACTIVITY_TIMEOUT);
        };

        const onPointerDown = (e: PointerEvent) => {
            if (ignore || !isTouchLikePointer(e) || !e.isPrimary) {
                return;
            }
            tapStartTime = performance.now();
            tapStartX = e.clientX;
            tapStartY = e.clientY;
        };

        const onPointerUp = (e: PointerEvent) => {
            if (ignore || !isTouchLikePointer(e) || !e.isPrimary) {
                return;
            }
            const tapDuration = performance.now() - tapStartTime;
            const dx = e.clientX - tapStartX;
            const dy = e.clientY - tapStartY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (tapDuration > TAP_MAX_DURATION || distance > TAP_MAX_DISTANCE) {
                return;
            }
            if (document.body.dataset.uiHidden !== undefined) {
                clearTimeout(timeout);
                delete document.body.dataset.uiHidden;
                autoHideUIInterfacesStateSignal.emit('visible');
                shouldDisplay?.(true);
                timeout = setTimeout(timeoutHandler, UI_INACTIVITY_TIMEOUT);
            } else {
                document.body.dataset.uiHidden = '';
                autoHideUIInterfacesStateSignal.emit('hidden');
                shouldDisplay?.(false);
            }
        };

        const throttled = throttle(onPointerMoveCallback, 500);

        const tabKeydown = (e: KeyboardEvent) => {
            if (e.code === 'Tab') {
                onPointerMoveCallback();
            }
        };

        if (!isDisabledByConfig && isStreamReady && enabled) {
            timeout = setTimeout(timeoutHandler, UI_INACTIVITY_TIMEOUT);
            document.addEventListener('pointermove', throttled);
            document.addEventListener('pointerdown', onPointerDown);
            document.addEventListener('pointerup', onPointerUp);
        }

        return () => {
            ignore = true;
            delete document.body.dataset.uiHidden;
            autoHideUIInterfacesStateSignal.emit('visible');
            clearTimeout(timeout);
            document.removeEventListener('keydown', tabKeydown);
            document.removeEventListener('pointermove', throttled);
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('pointerup', onPointerUp);
        };
    }, [
        shouldDisplay,
        enabled,
        isStreamReady,
        isDisabledByConfig,
        isTouchLikePointer,
    ]);

    return {enableAutoHide: setEnabled, enableAutoHideConstraintBased};
};
